import {
  WsmFeeBasis,
  WsmFeeCreateInput,
  WsmFeeFragment,
  WsmFeeScope,
  WsmFeeUpdateInput,
  WsmOptionSetCreateInput,
  WsmOptionSetDetailsFragment,
  WsmOptionSetPromptType,
  WsmOptionSetUpdateInput,
  WsmOptionValueFragment,
  WsmOptionValueInput,
  WsmOptionValueTierPriceInput,
  WsmProductComplianceFragment,
  WsmProductComplianceInput,
} from "@dashboard/graphql";

import {
  WsmComplianceFormData,
  WsmFeeFormData,
  WsmOptionSetFormData,
  WsmOptionValueDraft,
} from "./types";

let draftKeySeq = 0;

/** A handle unique within one form, and so within the one save it submits. */
export const makeDraftKey = (prefix: string) => `${prefix}-${++draftKeySeq}`;

// --- option sets -----------------------------------------------------------

/** What a shopper sees: the label, and the internal name only when it is empty. */
export const getOptionSetLabel = (optionSet: { name: string; label: string }) =>
  optionSet.label || optionSet.name;

/**
 * The gap the fleet's questions are numbered on: Select Year at 10, the next at
 * 20. A new question goes AFTER the ones a product already asks, never at 0,
 * because 0 is what an empty field parses to and it silently reorders the
 * shopper's build in front of every question already there.
 */
export const OPTION_SET_POSITION_STEP = 10;

/** Where a new question starts on a product that already asks these positions. */
export const defaultOptionSetPosition = (positions: number[]): number =>
  Math.max(
    OPTION_SET_POSITION_STEP,
    ...positions.map(position => position + OPTION_SET_POSITION_STEP),
  );

export const emptyOptionSetFormData = (): WsmOptionSetFormData => ({
  products: [],
  name: "",
  label: "",
  promptType: WsmOptionSetPromptType.CHOICE_ONE,
  required: false,
  note: "",
  sortOrder: OPTION_SET_POSITION_STEP,
  values: [],
});

export const valueToDraft = (value: WsmOptionValueFragment): WsmOptionValueDraft => ({
  key: makeDraftKey("value"),
  id: value.id,
  name: value.name,
  skuFragment: value.skuFragment,
  // Verbatim off the wire, signed, never reshaped.
  priceDelta: String(value.priceDelta ?? ""),
  imageUrl: value.imageUrl,
  sortOrder: value.sortOrder,
  tierDeltas: value.tierDeltas.map(delta => ({
    id: delta.id,
    dealerGroup: delta.dealerGroup.id,
    priceDelta: String(delta.priceDelta ?? ""),
  })),
});

export const optionSetToFormData = (
  optionSet: WsmOptionSetDetailsFragment,
): WsmOptionSetFormData => ({
  products: optionSet.products.map(product => ({ id: product.id, name: product.name })),
  name: optionSet.name,
  label: optionSet.label,
  promptType: optionSet.promptType,
  required: optionSet.required,
  note: optionSet.note,
  sortOrder: optionSet.sortOrder,
  values: optionSet.values.map(valueToDraft),
});

export const emptyOptionValueDraft = (sortOrder: number): WsmOptionValueDraft => ({
  key: makeDraftKey("value"),
  id: null,
  name: "",
  skuFragment: "",
  priceDelta: "",
  imageUrl: "",
  sortOrder,
  tierDeltas: [],
});

/** The delta one dealer group pays on one choice, empty when the group has none. */
export const getTierDelta = (value: WsmOptionValueDraft, dealerGroup: string): string =>
  value.tierDeltas.find(delta => delta.dealerGroup === dealerGroup)?.priceDelta ?? "";

/**
 * Edits one dealer group's delta on one choice. Emptying the cell REMOVES the
 * group's row rather than storing a zero: an absent row means the group pays
 * the retail delta, a stored zero means it pays nothing extra, and the
 * storefront prices those two differently.
 */
export const setTierDelta = (
  value: WsmOptionValueDraft,
  dealerGroup: string,
  raw: string,
): WsmOptionValueDraft => {
  const priceDelta = raw.trim();

  if (!priceDelta) {
    return {
      ...value,
      tierDeltas: value.tierDeltas.filter(delta => delta.dealerGroup !== dealerGroup),
    };
  }

  if (value.tierDeltas.some(delta => delta.dealerGroup === dealerGroup)) {
    return {
      ...value,
      tierDeltas: value.tierDeltas.map(delta =>
        delta.dealerGroup === dealerGroup ? { ...delta, priceDelta } : delta,
      ),
    };
  }

  // Appended, never inserted: the grid reads a group's cell by the group's key,
  // so position here carries no meaning. A new row has no id, which is what
  // tells the backend to create it.
  return {
    ...value,
    tierDeltas: [...value.tierDeltas, { id: null, dealerGroup, priceDelta }],
  };
};

const getTierDeltaInputs = (value: WsmOptionValueDraft): WsmOptionValueTierPriceInput[] =>
  value.tierDeltas.map(delta => ({
    ...(delta.id ? { id: delta.id } : {}),
    dealerGroup: delta.dealerGroup,
    priceDelta: delta.priceDelta.trim(),
  }));

/**
 * Every choice the question has, in the order the grid shows them. The list is
 * always sent whole, because the contract's `values` REPLACES it and the
 * configured-floor rule is checked across every row at once: a row left out
 * here is a row deleted there.
 */
export const getOptionValueInputs = (values: WsmOptionValueDraft[]): WsmOptionValueInput[] =>
  values.map((value, index) => ({
    ...(value.id ? { id: value.id } : {}),
    name: value.name.trim(),
    skuFragment: value.skuFragment.trim(),
    // An empty retail delta is no change to the price, which is zero, not null.
    priceDelta: value.priceDelta.trim() || "0",
    imageUrl: value.imageUrl.trim(),
    // The grid's own order IS the sort order: a merchant who moved a choice has
    // said what they mean, and re-sending the stored number would undo it.
    sortOrder: index,
    // Always provided, never omitted: omitting it on a choice whose last dealer
    // delta was just cleared would leave the cleared row stored.
    tierDeltas: getTierDeltaInputs(value),
  }));

const sameTierDeltas = (a: WsmOptionValueDraft, b: WsmOptionValueDraft) =>
  a.tierDeltas.length === b.tierDeltas.length &&
  a.tierDeltas.every(
    delta => getTierDelta(b, delta.dealerGroup).trim() === delta.priceDelta.trim(),
  );

const sameValue = (a: WsmOptionValueDraft, b: WsmOptionValueDraft) =>
  a.name === b.name &&
  a.skuFragment.trim() === b.skuFragment.trim() &&
  a.priceDelta.trim() === b.priceDelta.trim() &&
  a.imageUrl.trim() === b.imageUrl.trim() &&
  sameTierDeltas(a, b);

/**
 * How many choices one save would change, counted against what the server
 * handed back. A merchant editing a price list needs to know the grid is
 * holding edits before they navigate away from it.
 */
export const countValueChanges = (
  original: WsmOptionValueDraft[],
  draft: WsmOptionValueDraft[],
): number => {
  const originalById = new Map(original.filter(value => value.id).map(value => [value.id, value]));
  const keptIds = new Set(draft.filter(value => value.id).map(value => value.id));
  const added = draft.filter(value => !value.id).length;
  const removed = original.filter(value => value.id && !keptIds.has(value.id)).length;
  const edited = draft.filter(value => {
    const before = value.id ? originalById.get(value.id) : undefined;

    return !!before && !sameValue(before, value);
  }).length;

  return added + removed + edited;
};

/** The products a save attaches the question to, by key, in the order picked. */
const getProductInputs = (data: WsmOptionSetFormData): string[] =>
  data.products.map(product => product.id);

export const getOptionSetCreateInput = (
  data: WsmOptionSetFormData,
): WsmOptionSetCreateInput => ({
  products: getProductInputs(data),
  name: data.name.trim(),
  label: data.label.trim(),
  promptType: data.promptType,
  required: data.required,
  note: data.note.trim(),
  sortOrder: data.sortOrder,
  values: getOptionValueInputs(data.values),
});

export const getOptionSetUpdateInput = (data: WsmOptionSetFormData): WsmOptionSetUpdateInput => ({
  // Always sent, including empty: the list is REPLACED, and omitting it on a
  // question whose last product was just removed would leave it attached.
  products: getProductInputs(data),
  name: data.name.trim(),
  label: data.label.trim(),
  promptType: data.promptType,
  required: data.required,
  note: data.note.trim(),
  sortOrder: data.sortOrder,
  values: getOptionValueInputs(data.values),
});

// --- fees ------------------------------------------------------------------

export const emptyFeeFormData = (): WsmFeeFormData => ({
  label: "",
  sku: "",
  basis: WsmFeeBasis.FIXED,
  amount: "",
  applyTo: WsmFeeScope.PER_UNIT,
  required: false,
  declineLabel: "",
});

export const feeToFormData = (fee: WsmFeeFragment): WsmFeeFormData => ({
  label: fee.label,
  sku: fee.sku,
  basis: fee.basis,
  // Verbatim: one column carries money under FIXED and a percentage under
  // PERCENT, and the server owns both refusals.
  amount: String(fee.amount ?? ""),
  applyTo: fee.applyTo,
  required: fee.required,
  declineLabel: fee.declineLabel,
});

export const getFeeCreateInput = (data: WsmFeeFormData, product: string): WsmFeeCreateInput => ({
  product,
  label: data.label.trim(),
  sku: data.sku.trim(),
  basis: data.basis,
  amount: data.amount.trim(),
  applyTo: data.applyTo,
  required: data.required,
  declineLabel: data.declineLabel.trim(),
});

export const getFeeUpdateInput = (data: WsmFeeFormData): WsmFeeUpdateInput => ({
  label: data.label.trim(),
  sku: data.sku.trim(),
  basis: data.basis,
  amount: data.amount.trim(),
  applyTo: data.applyTo,
  required: data.required,
  declineLabel: data.declineLabel.trim(),
});

/**
 * What the amount field is denominated in. A percentage fee is not money, which
 * is why one label cannot be derived from the product's currency alone.
 */
export const getFeeAmountUnit = (basis: WsmFeeBasis, currencyCode: string | null) =>
  basis === WsmFeeBasis.PERCENT ? "%" : (currencyCode ?? "");

// --- product compliance ----------------------------------------------------

/**
 * Two-letter US codes out of whatever the merchant typed. Upper-cased and
 * de-duplicated in the order given; the server owns UNKNOWN_US_STATE_CODE,
 * because a client-side list of valid codes goes stale and refuses a
 * territory the model accepts.
 */
export const parseStateCodes = (raw: string): string[] =>
  raw
    .split(/[\s,;]+/)
    .map(part => part.trim().toUpperCase())
    .filter(part => part.length > 0)
    .reduce<string[]>((acc, code) => (acc.includes(code) ? acc : [...acc, code]), []);

export const formatStateCodes = (codes: string[]) => codes.join(", ");

/**
 * What a shipping zone enforces, which is its countries and never its name. A
 * zone called "Continental US" still carries Alaska and Hawaii, because Saleor
 * zones hold countries and nothing narrower. Restricted states is the field
 * that does state-level work.
 */
export const formatZoneCountries = (zone: {
  countries?: Array<{ country: string }> | null;
}): string => (zone.countries ?? []).map(country => country.country).join(", ");

export const emptyComplianceFormData = (): WsmComplianceFormData => ({
  prop65: false,
  prop65Text: "",
  restrictedStates: [],
  includeShippingZones: [],
  restrictionMessage: "",
});

export const complianceToFormData = (
  compliance: WsmProductComplianceFragment,
): WsmComplianceFormData => ({
  prop65: compliance.prop65,
  prop65Text: compliance.prop65Text,
  restrictedStates: compliance.restrictedStates,
  includeShippingZones: compliance.includeShippingZones.map(zone => zone.id),
  restrictionMessage: compliance.restrictionMessage,
});

export const getComplianceInput = (data: WsmComplianceFormData): WsmProductComplianceInput => ({
  prop65: data.prop65,
  prop65Text: data.prop65Text.trim(),
  restrictedStates: data.restrictedStates,
  // Always sent, including empty: an empty list is how a merchant clears a
  // zone restriction, and omitting it would leave the old set stored.
  includeShippingZones: data.includeShippingZones,
  restrictionMessage: data.restrictionMessage.trim(),
});
